/**
 * @voltxampere/engine — Worker request handler (Sprint 9)
 *
 * Dispatches a parsed {@link EngineRequest} to the right engine
 * code path and shepherds the result back out through the
 * caller's `postMessage` function. Kept transport-agnostic so the
 * same handler runs inside a Web Worker (production), a Node
 * `worker_threads` worker (server-side tests), or a jsdom test
 * harness (integration tests).
 *
 * Contract:
 *   - `handleRequest` never throws — it always posts a typed
 *     response, including a typed `error` envelope for unexpected
 *     failures.
 *   - Long-running analyses register an `AbortController` in the
 *     state's `pendingCancellations` map keyed by the request id.
 *     A `cancel` request looks them up and aborts.
 *   - `destroy` clears timers, aborts pending work, posts
 *     `destroyed`, and signals the caller to terminate the
 *     transport.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { PACKAGE_VERSION } from "../index.js";
import { DCSolver } from "../dc/solver.js";
import { TransientSolver } from "../transient/solver.js";
import { ACSolver } from "../ac/solver.js";
import { registerACStamps, registerDCStamps, registerTransientStamps } from "../stamps/register.js";
import { runAssertions } from "../assertion/run.js";
import { makeWorkerState } from "./state.js";
function errorResponse(id, err) {
    return { type: "error", id, error: err };
}
function toEngineErrorCode(name) {
    switch (name) {
        case "AbortError":
            return "CANCELLED";
        case "ConvergenceError":
            return "CONVERGENCE_FAILED";
        case "TopologyError":
            return "TOPOLOGY_ERROR";
        case "InvariantError":
            return "INVALID_REQUEST";
        default:
            return "UNKNOWN";
    }
}
function serialiseError(err) {
    if (err instanceof Error) {
        return {
            code: toEngineErrorCode(err.name),
            message: err.message,
            ...(err.stack !== undefined ? { stack: err.stack } : {}),
        };
    }
    return { code: "UNKNOWN", message: String(err) };
}
function mapToRecord(m) {
    const out = {};
    for (const [k, v] of m)
        out[k] = v;
    return out;
}
// Sprint 10.0 (B-P1-03 closure): `result.assertionResults` is
// populated by `DCSolver.buildResult` via `runAssertionsOnDC` on
// every solve. With Sprint 10's optional `assertionResults` field
// on `AnalysisResultSchema`, the wire now carries the outcomes
// inline so clients can read them from `runAnalysis`'s own response
// without a follow-up `runAssertions` round trip. The dedicated
// `runAssertions` request stays for the cached-result re-evaluation
// case but is no longer the only path.
function dcResultToWire(kind, result) {
    return {
        kind,
        converged: result.converged,
        iterations: result.iterations,
        nodeVoltages: mapToRecord(result.nodeVoltages),
        branchCurrents: mapToRecord(result.sourceCurrents),
        warnings: result.warnings.map((w) => w.message),
        meta: {
            fallback: result.usedFallback,
            clampEvents: result.clampEvents,
            armijoEvents: result.armijoEvents,
            convergenceTier: result.convergenceTier,
            finalResidualNorm: result.finalResidualNorm,
        },
        ...(result.assertionResults.length > 0
            ? { assertionResults: result.assertionResults.map(assertionResultToWire) }
            : {}),
    };
}
/**
 * Sprint 20: project an `ACResult` onto the wire envelope. AC traces
 * are per-frequency arrays — too rich for the scalar `nodeVoltages` /
 * `branchCurrents` records — so we land them on `meta` (the open
 * `z.record(string, unknown)` bag, see ADR-0012). The scalar maps
 * are intentionally left empty so DC/transient consumers do not
 * misinterpret them.
 *
 * Wire layout (consumed by `apps/web/src/store/ac-runner.ts`):
 *   meta.acFrequencies: number[]
 *   meta.acNodeMagnitudes: Record<NetId, number[]>
 *   meta.acNodePhases:     Record<NetId, number[]>
 *   meta.acBranchMagnitudes: Record<ComponentId, number[]>
 *   meta.acBranchPhases:     Record<ComponentId, number[]>
 *   meta.dcOPElapsedMs / meta.sweepElapsedMs / meta.sweepPoints
 */
function acResultToWire(kind, result) {
    const f = Array.from(result.frequencies);
    const traceMapToRecord = (map) => {
        const out = {};
        for (const [k, v] of map)
            out[k] = Array.from(v);
        return out;
    };
    return {
        kind,
        converged: result.converged,
        iterations: 0,
        nodeVoltages: {},
        branchCurrents: {},
        warnings: [...result.warnings],
        meta: {
            acFrequencies: f,
            acNodeMagnitudes: traceMapToRecord(result.nodeMagnitudes),
            acNodePhases: traceMapToRecord(result.nodePhases),
            acBranchMagnitudes: traceMapToRecord(result.branchMagnitudes),
            acBranchPhases: traceMapToRecord(result.branchPhases),
            dcOPElapsedMs: result.meta.dcOPElapsedMs,
            sweepElapsedMs: result.meta.sweepElapsedMs,
            sweepPoints: result.meta.sweepPoints,
        },
    };
}
function transientResultToWire(kind, result) {
    const last = result.timeSteps[result.timeSteps.length - 1];
    // Sprint 15 (D9, ADR-0027): additive metadata extension. The internal
    // TransientResult.timeSteps[] carries the full per-step Map<netId, V>
    // time series; the pre-Sprint-15 wire flattened to last-step only
    // because Sprint 9's protocol was DC-shaped. Waveform Viewer needs
    // the per-step samples, so we project them into `meta.timeSeries` as
    // plain JSON. Algorithm is untouched — this is serialisation only.
    // Backwards compatible: the AnalysisResultSchema.meta is already a
    // `z.record(z.string(), z.unknown())` open bag, and pre-Sprint-15
    // clients that read only `nodeVoltages` keep working unchanged.
    const timeSeries = result.timeSteps.map((step) => ({
        t: step.t,
        nodeVoltages: mapToRecord(step.nodeVoltages),
        sourceCurrents: mapToRecord(step.sourceCurrents),
    }));
    return {
        kind,
        converged: result.converged,
        iterations: result.totalNewtonIterations,
        nodeVoltages: last ? mapToRecord(last.nodeVoltages) : {},
        branchCurrents: last ? mapToRecord(last.sourceCurrents) : {},
        warnings: [],
        meta: {
            samples: result.timeSteps.length,
            stepsAccepted: result.stepsAccepted,
            stepsRejected: result.stepsRejected,
            method: result.method,
            dcOperatingPointFallback: result.dcOperatingPointFallback,
            totalClampEvents: result.totalClampEvents,
            timeSeries,
        },
        // Sprint 10.0 (B-P1-03 closure): same inline-assertion treatment
        // as the DC wire, sourcing from the transient solver's auto-run.
        ...(result.assertionResults.length > 0
            ? { assertionResults: result.assertionResults.map(assertionResultToWire) }
            : {}),
    };
}
function assertionResultToWire(r) {
    return {
        assertionId: r.assertionId,
        sourceText: r.sourceText,
        passed: r.passed,
        skipped: r.skipped,
        message: r.message,
        actual: r.actual,
        expected: r.expected,
        margin: r.margin,
        marginUnit: r.marginUnit,
        analysisKind: r.analysisKind,
        ...(r.resolutionError !== undefined ? { resolutionError: r.resolutionError } : {}),
    };
}
function frameFromDc(result) {
    return {
        timestamp: Date.now() / 1000,
        nodeVoltages: mapToRecord(result.nodeVoltages),
        branchCurrents: mapToRecord(result.sourceCurrents),
        converged: result.converged,
        iterations: result.iterations,
        warnings: result.warnings.map((w) => w.code),
    };
}
export async function handleRequest(req, deps) {
    const { state, postMessage } = deps;
    const setIntervalFn = deps.setIntervalFn ?? setInterval;
    const clearIntervalFn = deps.clearIntervalFn ?? clearInterval;
    try {
        switch (req.type) {
            case "init": {
                state.circuit = req.circuit;
                if (req.options?.registerStamps !== false) {
                    registerDCStamps();
                    registerTransientStamps();
                    registerACStamps();
                }
                state.initialised = true;
                postMessage({ type: "ready", id: req.id, engineVersion: PACKAGE_VERSION });
                return;
            }
            case "updateCircuit": {
                if (!state.initialised) {
                    postMessage(errorResponse(req.id, {
                        code: "NOT_INITIALISED",
                        message: "updateCircuit before init",
                    }));
                    return;
                }
                state.circuit = req.circuit;
                state.lastDc = null;
                state.lastTransient = null;
                state.lastAc = null;
                postMessage({ type: "ready", id: req.id, engineVersion: PACKAGE_VERSION });
                return;
            }
            case "runAnalysis": {
                if (!state.initialised || !state.circuit) {
                    postMessage(errorResponse(req.id, {
                        code: "NOT_INITIALISED",
                        message: "runAnalysis before init",
                    }));
                    return;
                }
                const ctrl = new AbortController();
                state.pendingCancellations.set(req.id, ctrl);
                try {
                    const result = await runAnalysisInternal(state, req.analysis, ctrl.signal, (pct, stage) => {
                        postMessage({
                            type: "progress",
                            id: req.id,
                            analysis: req.analysis.kind,
                            percent: pct,
                            stage,
                        });
                    });
                    postMessage({ type: "analysisResult", id: req.id, result });
                }
                finally {
                    state.pendingCancellations.delete(req.id);
                }
                return;
            }
            case "runAssertions": {
                if (!state.initialised || !state.circuit) {
                    postMessage(errorResponse(req.id, {
                        code: "NOT_INITIALISED",
                        message: "runAssertions before init",
                    }));
                    return;
                }
                const snapshot = req.analysis === "DC" && state.lastDc
                    ? { dc: state.lastDc }
                    : req.analysis === "TRANSIENT" && state.lastTransient
                        ? { transient: state.lastTransient }
                        : null;
                if (!snapshot) {
                    postMessage(errorResponse(req.id, {
                        code: "NOT_INITIALISED",
                        message: `no ${req.analysis} result cached — call runAnalysis first`,
                    }));
                    return;
                }
                const results = runAssertions({
                    circuit: state.circuit,
                    snapshot,
                    analysisKind: req.analysis,
                });
                postMessage({
                    type: "assertionResult",
                    id: req.id,
                    results: results.map(assertionResultToWire),
                });
                return;
            }
            case "startStream": {
                if (!state.initialised || !state.circuit) {
                    postMessage(errorResponse(req.id, {
                        code: "NOT_INITIALISED",
                        message: "startStream before init",
                    }));
                    return;
                }
                if (state.stream !== null) {
                    postMessage(errorResponse(req.id, {
                        code: "STREAM_ALREADY_RUNNING",
                        message: "another stream is already running",
                    }));
                    return;
                }
                const intervalMs = Math.max(1, Math.round(1000 / req.fps));
                // Sprint 10.0 (B-P1-02 closure): per-stream AbortController.
                // `stopStream` and `destroy` abort it so an in-flight tick
                // whose solve is mid-Newton (a) short-circuits at the next
                // strategy boundary inside `DCSolver.solve` and (b) suppresses
                // its post-solve frame so no `frame` arrives after the
                // `streamStopped` / `destroyed` envelope.
                const abortController = new AbortController();
                const streamSignal = abortController.signal;
                // ESLint's `no-unnecessary-condition` narrows
                // `streamSignal.aborted` to `false` because the controller
                // is fresh in this scope. At runtime the property flips when
                // `stopStream` / `destroy` invokes `abortController.abort()`
                // from a sibling handler arm; the static narrowing is wrong.
                // We wrap the read in a tiny helper so the lint rule doesn't
                // see the literal-false type at the callsite.
                const isStreamAborted = () => streamSignal.aborted;
                const tick = async () => {
                    if (!state.circuit)
                        return;
                    if (isStreamAborted())
                        return;
                    try {
                        const dc = await new DCSolver().solve(state.circuit, {
                            signal: streamSignal,
                        });
                        // Suppress the post if the stream was stopped/destroyed
                        // while the solve was in flight. The solve itself may
                        // have completed (DCSolver only polls the signal between
                        // strategies); we still must not deliver a frame after
                        // the wire-level stream lifetime ended.
                        if (isStreamAborted())
                            return;
                        state.lastDc = dc;
                        const frame = frameFromDc(dc);
                        postMessage({ type: "frame", id: req.id, timestamp: frame.timestamp, state: frame });
                    }
                    catch (err) {
                        // Cancellation isn't a stream failure — silently swallow
                        // when the abort raised the AbortError. Any other error
                        // gets the existing STREAM_SOLVE_FAIL envelope.
                        if (isStreamAborted())
                            return;
                        postMessage(errorResponse(req.id, {
                            code: "STREAM_SOLVE_FAIL",
                            message: err instanceof Error ? err.message : String(err),
                        }));
                    }
                };
                const timer = setIntervalFn(() => {
                    void tick();
                }, intervalMs);
                state.stream = { id: req.id, intervalMs, timer, abortController };
                // Emit one frame immediately so tests don't have to wait
                // for the first tick to validate the wiring.
                await tick();
                return;
            }
            case "stopStream": {
                if (state.stream === null) {
                    postMessage(errorResponse(req.id, {
                        code: "STREAM_NOT_RUNNING",
                        message: "no stream to stop",
                    }));
                    return;
                }
                // Sprint 10.0 (B-P1-02 closure): abort BEFORE clearing the
                // interval so any tick that is currently between schedule
                // and execution sees the aborted signal and bails before
                // its post. Tick functions check `streamSignal.aborted`
                // both before posting frame and inside their catch arm.
                state.stream.abortController.abort();
                clearIntervalFn(state.stream.timer);
                state.stream = null;
                // Sprint 10.0 (B-P1-01 closure): dedicated `streamStopped`
                // envelope so wire logs distinguish stream lifecycle from
                // worker lifecycle. `ts` matches `frame.timestamp` units
                // (wall-clock seconds).
                postMessage({ type: "streamStopped", id: req.id, ts: Date.now() / 1000 });
                return;
            }
            case "cancel": {
                const ctrl = state.pendingCancellations.get(req.targetId);
                if (ctrl) {
                    ctrl.abort();
                    state.pendingCancellations.delete(req.targetId);
                }
                postMessage({ type: "cancelled", id: req.id, targetId: req.targetId });
                return;
            }
            case "destroy": {
                if (state.stream) {
                    // Sprint 10.0 (B-P1-02 closure): abort the stream's
                    // controller so an in-flight tick mid-solve does not post
                    // a frame AFTER this `destroyed` envelope.
                    state.stream.abortController.abort();
                    clearIntervalFn(state.stream.timer);
                    state.stream = null;
                }
                for (const ctrl of state.pendingCancellations.values()) {
                    ctrl.abort();
                }
                state.pendingCancellations.clear();
                state.initialised = false;
                state.circuit = null;
                state.lastDc = null;
                state.lastTransient = null;
                state.lastAc = null;
                postMessage({ type: "destroyed", id: req.id });
                return;
            }
        }
    }
    catch (err) {
        postMessage(errorResponse(req.id, serialiseError(err)));
    }
}
async function runAnalysisInternal(state, analysis, signal, onProgress) {
    if (!state.circuit) {
        throw new Error("runAnalysisInternal called without an initialised circuit");
    }
    if (analysis.kind === "DC") {
        const dc = await new DCSolver().solve(state.circuit, { signal, onProgress });
        state.lastDc = dc;
        return dcResultToWire("DC", dc);
    }
    if (analysis.kind === "TRANSIENT") {
        // tstop override lives on the circuit's settings; apply via
        // a shallow copy so the stored circuit is untouched.
        const circuitForRun = analysis.tstop
            ? {
                ...state.circuit,
                simulationSettings: {
                    ...state.circuit.simulationSettings,
                    transient: {
                        ...state.circuit.simulationSettings.transient,
                        tstop: analysis.tstop,
                    },
                },
            }
            : state.circuit;
        const tr = await new TransientSolver().solve(circuitForRun, { signal, onProgress });
        state.lastTransient = tr;
        return transientResultToWire("TRANSIENT", tr);
    }
    // Sprint 20: AC arm. The `AnalysisKindSchema` enum is now
    // exhaustive (DC + TRANSIENT + AC), so we drop the trailing
    // `throw "not implemented"` — the `if` chain above either
    // returned for DC / TRANSIENT, or falls through to here for AC.
    // A future kind would surface a TypeScript exhaustiveness error
    // at the union narrowing rather than an unreachable runtime throw.
    {
        // Per-run override merged from the
        // circuit's `simulationSettings.ac` defaults; absent fields fall
        // through to the schema defaults (1 Hz, 1 GHz, 100 points, dec).
        const baseAc = state.circuit.simulationSettings.ac;
        const sweepOverride = analysis.acSweep !== undefined
            ? {
                fStart: analysis.acSweep.fStart ?? baseAc.start,
                fStop: analysis.acSweep.fStop ?? baseAc.stop,
                points: analysis.acSweep.points ?? baseAc.points,
                sweep: analysis.acSweep.sweep ?? (baseAc.sweep === "lin" ? "lin" : "dec"),
            }
            : undefined;
        // Sprint 80.Z L-AYARLAR-5 — thread `circuit.simulationSettings.
        // dc.temperature` (Celsius) into the AC solver. Without this the
        // AYARLAR > Sıcaklık preference reaches the DC pre-pass but the
        // AC small-signal stamps stay at the hardcoded 300.15 K default.
        // Conversion: `T_K = T_C + 273.15`. Stays bit-identical at the
        // 27 °C SPICE-canonical default (300.15 K = the existing default).
        const acTempK = state.circuit.simulationSettings.dc.temperature + 273.15;
        const acOpts = {
            signal,
            onProgress,
            temperature: acTempK,
            ...(sweepOverride !== undefined ? { sweep: sweepOverride } : {}),
        };
        const ac = await new ACSolver().solve(state.circuit, acOpts);
        state.lastAc = ac;
        // Stamp registry warmup: the ACSolver self-registers, but
        // explicit calls keep the worker init cheaper for a follow-up
        // run that hits the cached path.
        registerACStamps();
        return acResultToWire("AC", ac);
    }
}
/** Convenience factory the worker entry point uses. */
export function createHandlerDeps(postMessage) {
    return { state: makeWorkerState(), postMessage };
}
//# sourceMappingURL=handler.js.map